import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import { useDispatch } from '@tmapy/redux';

import { ExportToolForm } from './ExportToolForm';
import {
  changeDeleteSuccessAlertVisibility,
  changeSubmitSuccessAlertVisibility,
} from '../../app/actions';
import { useHistoryAction } from '../../hooks';
import { EXPORT_TOOL_FORM_TYPE } from '../constants';
import { QUERY_STATE } from '../../constants';
import { actionResourceInvalidate } from '../../redux/resource/actions';

import { useExportToolsRetrieveResource } from '../../openapi/operations/exportToolsRetrieve';
import {
  exportToolsCreate,
  ExportToolsCreateRequest,
} from '../../openapi/operations/exportToolsCreate';
import { exportToolsUpdate } from '../../openapi/operations/exportToolsUpdate';
import { exportToolsDestroy } from '../../openapi/operations/exportToolsDestroy';
import { exportToolsSetDefaultCreate } from '../../openapi/operations/exportToolsSetDefaultCreate';
import { exportToolsListOperation } from '../../openapi/operations/exportToolsList';

export const ExportTool: React.FC = () => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id?: string }>();
  const exportToolId = id ? parseInt(id, 10) : null;
  const formType = exportToolId ? EXPORT_TOOL_FORM_TYPE.EDIT : EXPORT_TOOL_FORM_TYPE.NEW;
  const resource = useExportToolsRetrieveResource(exportToolId ? { id: exportToolId } : null);

  const routeToExportToolList = useHistoryAction('vydejExportToolList');

  const [submitQueryState, setSubmitQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [deleteQueryState, setDeleteQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);

  const invalidateList = useCallback(() => {
    dispatch(actionResourceInvalidate(exportToolsListOperation({}).path));
  }, [dispatch]);

  const onSubmit = useCallback(
    async (values: ExportToolsCreateRequest) => {
      try {
        setSubmitQueryState(QUERY_STATE.WAITING);
        if (exportToolId) {
          // Data se vždy ukládají přes update - set_default tělo requestu ignoruje,
          // takže by se změny ostatních polí (např. tool_name) tiše zahodily.
          const response = await exportToolsUpdate({
            query: { id: exportToolId },
            request: values,
          });
          // Zapnutý přepínač „Výchozí pro formát" se teprve poté potvrdí přes set_default,
          // který serverově odznačí ostatní záznamy stejného formátu.
          const setDefaultResponse =
            response.ok && values.is_default
              ? await exportToolsSetDefaultCreate({
                  query: { id: exportToolId },
                  request: values,
                })
              : null;
          if (response.ok && (setDefaultResponse?.ok ?? true)) {
            setSubmitQueryState(QUERY_STATE.SUCCESS);
            resource.refetch();
          } else {
            setSubmitQueryState(QUERY_STATE.FAIL);
          }
        } else {
          const response = await exportToolsCreate({ request: values });
          if (response.ok) {
            setSubmitQueryState(QUERY_STATE.SUCCESS);
            dispatch(changeSubmitSuccessAlertVisibility(true));
            routeToExportToolList();
          } else {
            setSubmitQueryState(QUERY_STATE.FAIL);
          }
        }
        invalidateList();
      } catch (e) {
        setSubmitQueryState(QUERY_STATE.FAIL);
      }
    },
    [exportToolId, dispatch, routeToExportToolList, invalidateList, resource],
  );

  const onDelete = useCallback(async () => {
    try {
      setDeleteQueryState(QUERY_STATE.WAITING);
      const response = await exportToolsDestroy({ query: { id: exportToolId! } });
      if (response.ok) {
        dispatch(changeDeleteSuccessAlertVisibility(true));
        invalidateList();
        routeToExportToolList();
        setDeleteQueryState(QUERY_STATE.SUCCESS);
      } else {
        setDeleteQueryState(QUERY_STATE.FAIL);
      }
    } catch (e) {
      setDeleteQueryState(QUERY_STATE.FAIL);
    }
  }, [exportToolId, dispatch, routeToExportToolList, invalidateList]);

  return (
    <ExportToolForm
      key={`${resource.data?.id}-${resource.fetchedAt}`}
      formType={formType}
      resource={resource}
      submitQueryState={submitQueryState}
      deleteQueryState={deleteQueryState}
      onSubmit={onSubmit}
      onDelete={onDelete}
    />
  );
};
